import { useLocation } from "react-router-dom";
import React, { createContext, useEffect, useState, useMemo } from "react";
import { Outlet } from "react-router-dom";
import { Box, Container, Typography } from "@mui/material";
import QMentisBreadCrumb from "../custom-components/QMentisBreadCrumb";
import { ReactComponent as AutomateIcon } from "../../images/automateIcon.svg";
import FullscreenToggle from "../custom-components/FullScreenToggle";

// Contexts
export const SecurityFlowDataContext = createContext(null);
export const SecurityLoaderStateContext = createContext(null);

const SecurityTestingPage = () => {
  const [brd, setBRD] = useState({});
  const [userStory, setUserStory] = useState({});
  const [workFlow, setWorkFlow] = useState({});
  const [funcMod, setFuncMod] = useState("");
  const [secRecommendations, setSecRecommendations] = useState({});
  const [archDesign, setArchDesign] = useState("");
  const [threatModelling, setThreatModelling] = useState({});
  const [addReview, setAddReview] = useState({});
  const [securityTestPlan, setSecurityTestPlan] = useState({});
  const [securityReq, setSecurityReq] = useState([]);
  const [securityReqData, _setSecurityReqData] = useState([]);
  const [securityLoaderState, setSecurityLoaderState] = useState("");
  const [constructionPhase, setConstructionPhase] = useState([]);
  const [scanResults, setScanResults] = useState([]);
  const [date, setDate] = useState([]);

  const [dastScanResult, setDastScanResult] = useState([]);
  const [burpSuiteScan, setBurpSuiteScan] = useState([]);
  const [insightsPhase, setInsightsPhase] = useState({});

  const location = useLocation();
  const currentPath = location.pathname.split("/").pop();

  //  Transform function
  const transformSecurityData = (rawData) => {
    if (!Array.isArray(rawData)) {
      console.warn("Invalid rawData format: Expected an array.", rawData);
      return [];
    }

    return rawData.map((item) => ({
      "Module Name": item["Module Name"],
      "Security Requirements": item["Security Requirements"],
    }));
  };

  const setSecurityReqData = (rawData) => {
    const transformed = transformSecurityData(rawData);
    _setSecurityReqData(transformed);
    setSecurityReq(rawData);
  };

  //  BreadCrumbs calculation
  const breadCrumbs = useMemo(
    () => [
      {
        title: "Requirement Phase",
        status:
          archDesign && archDesign !== ""
            ? "Completed"
            : currentPath === "input"
              ? "In Progress"
              : "Not Started",
        path: currentPath,
      },
      {
        title: "Design Phase",
        status:
          addReview && Object.keys(addReview).length > 0
            ? "Completed"
            : securityLoaderState === "Design Phase" ||
              currentPath === "design-phase-output"
              ? "In Progress"
              : "Not Started",
        path: currentPath,
      },
      {
        title: "SAST Phase",
        status:
          securityLoaderState === "Construction Phase"
            ? "In Progress"
            : constructionPhase === null
              ? "Not Started"
              : constructionPhase && constructionPhase.length > 0
                ? "Completed"
                : "Not Started",
      },
      {
        title: "DAST Phase",
        status:
          dastScanResult && dastScanResult.length > 0
            ? "Completed"
            : securityLoaderState === "DAST Scan"
              ? "In Progress"
              : dastScanResult === null
                ? "Not Started"
                : "Not Started",
      },
      {
        title: "Insights Phase",
        status:
          securityLoaderState === "Insights Scan"
            ? "In Progress"
            : insightsPhase &&
              typeof insightsPhase === "object" &&
              Object.keys(insightsPhase).length > 0
              ? "Completed"
              : "Not Started",
      },
      {
        title: "Test Plan",
        status:
          securityLoaderState === "Insights Scan"
            ? "In Progress"
            : insightsPhase &&
              typeof insightsPhase === "object" &&
              Object.keys(insightsPhase).length > 0
              ? "Completed"
              : "Not Started",
      },
    ],
    [
      archDesign,
      funcMod,
      addReview,
      securityTestPlan,
      secRecommendations,
      securityLoaderState,
    ]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden", // Hide overflow on root to control scrolling
      }}
    >
      {currentPath !== "security-test-plan-home-page" && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            width: "100%",
            zIndex: 1100,
            bgcolor: "background.paper",
            boxShadow: 1,
            boxSizing: "border-box",
          }}
        >
          <QMentisBreadCrumb
            breadCrumbs={breadCrumbs}
            sx={{ height: { xs: "48px", sm: "56px" } }}
          />
        </Box>
      )}


      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
          px: { xs: 2, sm: 2, md: 2 },
          py: { xs: 1, sm: 1 },
          overflow: "auto", // Add scrollbar to main content area
          minHeight: 0, // Important: allows flex child to shrink
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            boxSizing: "border-box",
          }}
        >
          <SecurityLoaderStateContext.Provider
            value={{ securityLoaderState, setSecurityLoaderState }}
          >
            <SecurityFlowDataContext.Provider
              value={{
                brd,
                setBRD,
                userStory,
                setUserStory,
                workFlow,
                setWorkFlow,
                funcMod,
                setFuncMod,
                secRecommendations,
                setSecRecommendations,
                archDesign,
                setArchDesign,
                threatModelling,
                setThreatModelling,
                addReview,
                setAddReview,
                securityTestPlan,
                setSecurityTestPlan,
                securityReq,
                setSecurityReq,
                constructionPhase,
                setConstructionPhase,
                scanResults,
                setScanResults,
                date,
                setDate,
                dastScanResult,
                setDastScanResult,
                burpSuiteScan,
                setBurpSuiteScan,
                insightsPhase,
                setInsightsPhase,
              }}
            >
              <Outlet />
            </SecurityFlowDataContext.Provider>
          </SecurityLoaderStateContext.Provider>
        </Box>
      </Box>
    </Box>
  );
};

export default SecurityTestingPage;
